import { useState, useEffect, useCallback } from "react";

const FAVORITES_KEY = "directproprietaire_favorites";
const FAVORITES_EVENT = "directproprietaire_favorites_change";

// Helper pour lire la liste depuis localStorage
const getFavoritesFromStorage = (): string[] => {
  const stored = localStorage.getItem(FAVORITES_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Hook personnalisé pour gérer les annonces favorites dans localStorage
 * Permet aux visiteurs de sauvegarder des annonces sans inscription
 * Synchronise entre onglets (StorageEvent) et composants du même onglet (CustomEvent)
 */
export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>(getFavoritesFromStorage);

  // Synchronisation entre onglets (StorageEvent) et composants du même onglet (CustomEvent)
  useEffect(() => {
    // Gestionnaire pour les autres onglets
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === FAVORITES_KEY) {
        setFavorites(getFavoritesFromStorage());
      }
    };

    // Gestionnaire pour les composants du même onglet
    const handleFavoritesChange = () => {
      setFavorites(getFavoritesFromStorage());
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(FAVORITES_EVENT, handleFavoritesChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(FAVORITES_EVENT, handleFavoritesChange);
    };
  }, []);

  // Sauvegarde les favoris dans localStorage et notifie les autres composants
  const saveFavorites = useCallback((newFavorites: string[]) => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
    setFavorites(newFavorites);
    // Déclenche un événement pour notifier les autres composants du même onglet
    window.dispatchEvent(new CustomEvent(FAVORITES_EVENT));
  }, []);

  // Ajoute ou retire une annonce des favoris
  const toggleFavorite = useCallback(
    (annonceId: string) => {
      const isFavorite = favorites.includes(annonceId);
      const newFavorites = isFavorite
        ? favorites.filter((id) => id !== annonceId)
        : [...favorites, annonceId];
      saveFavorites(newFavorites);
      return !isFavorite;
    },
    [favorites, saveFavorites]
  );

  // Vérifie si une annonce est en favori
  const isFavorite = useCallback(
    (annonceId: string) => favorites.includes(annonceId),
    [favorites]
  );

  // Retire un favori spécifique
  const removeFavorite = useCallback(
    (annonceId: string) => {
      const newFavorites = favorites.filter((id) => id !== annonceId);
      saveFavorites(newFavorites);
    },
    [favorites, saveFavorites]
  );

  // Vide tous les favoris
  const clearFavorites = useCallback(() => {
    saveFavorites([]);
  }, [saveFavorites]);

  // Nombre de favoris
  const favoritesCount = favorites.length;

  return {
    favorites,
    toggleFavorite,
    isFavorite,
    removeFavorite,
    clearFavorites,
    favoritesCount,
  };
};

export default useFavorites;
